import { MenuItem, toast } from '@affine/component';
import { useGuard } from '@affine/core/components/guard';
import { useEnableCloud } from '@affine/core/components/hooks/affine/use-enable-cloud';
import { type Doc, DocsService } from '@affine/core/modules/doc';
import { type Editor, EditorsService } from '@affine/core/modules/editor';
import {
  beginObjectConfigurationTrace,
  recordObjectConfigurationTraceEvent,
} from '@affine/core/modules/permissions/utils/object-configuration-trace';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { ShareIcon } from '@blocksuite/icons/rc';
import { FrameworkScope, useServices } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DocConfigurationDialog } from './doc-configuration-dialog';

export const DocShareMenuItem = ({ docId }: { docId: string }) => {
  const t = useI18n();
  const canManageUsers = useGuard('Doc_Users_Manage', docId);
  const confirmEnableCloud = useEnableCloud();
  const { docsService, workspaceService } = useServices({
    DocsService,
    WorkspaceService,
  });
  const [open, setOpen] = useState(false);
  const [currentDoc, setCurrentDoc] = useState<Doc | null>(null);
  const [currentEditor, setCurrentEditor] = useState<Editor | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const releaseRef = useRef<(() => void) | null>(null);

  const closeLoadedDoc = useCallback(() => {
    editorRef.current?.dispose();
    editorRef.current = null;
    releaseRef.current?.();
    releaseRef.current = null;
    setCurrentEditor(null);
    setCurrentDoc(null);
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setOpen(false);
        closeLoadedDoc();
        return;
      }

      if (!currentDoc) {
        try {
          recordObjectConfigurationTraceEvent('doc-open-started');
          const { doc, release } = docsService.open(docId);
          releaseRef.current = release;
          recordObjectConfigurationTraceEvent('doc-open-finished');
          recordObjectConfigurationTraceEvent('editor-create-started');
          const editor = doc.scope.get(EditorsService).createEditor();
          recordObjectConfigurationTraceEvent('editor-create-finished');
          editorRef.current = editor;
          setCurrentDoc(doc);
          setCurrentEditor(editor);
        } catch (err) {
          closeLoadedDoc();
          console.error('Failed to open doc sharing:', err);
          toast(t['com.streamlain.share-menu.open-failed']());
          return;
        }
      }

      track.$.sharePanel.$.open();
      setOpen(true);
    },
    [closeLoadedDoc, currentDoc, docId, docsService, t]
  );

  useEffect(
    () => () => {
      editorRef.current?.dispose();
      editorRef.current = null;
      releaseRef.current?.();
      releaseRef.current = null;
    },
    []
  );

  if (canManageUsers !== true) {
    return null;
  }

  return (
    <>
      <MenuItem
        prefixIcon={<ShareIcon />}
        data-testid="doc-menu-share"
        onSelect={event => {
          // The dialog state lives in this menu item. Keep the parent menu
          // mounted while the portalled dialog is open.
          event.preventDefault();
          beginObjectConfigurationTrace('doc');
          handleOpenChange(true);
        }}
      >
        {t['com.streamlain.share-menu.shareButton']()}
      </MenuItem>
      {currentDoc && currentEditor ? (
        <FrameworkScope scope={currentDoc.scope}>
          <FrameworkScope scope={currentEditor.scope}>
            <DocConfigurationDialog
              open={open}
              onOpenChange={handleOpenChange}
              workspaceMetadata={workspaceService.workspace.meta}
              currentPage={currentDoc.blockSuiteDoc}
              hideExport
              onEnableAffineCloud={() =>
                confirmEnableCloud(workspaceService.workspace, {
                  openPageId: docId,
                })
              }
            />
          </FrameworkScope>
        </FrameworkScope>
      ) : null}
    </>
  );
};
